<template>
  <div class="home-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-overlay"></div>
      <v-container class="hero-content">
        <v-row align="center" justify="center" class="fill-height">
          <v-col cols="12" md="8" class="text-center">
            <div class="ornament-top mb-8" data-aos="fade-down">
              <v-icon color="gold" size="48">mdi-account-group</v-icon>
            </div>
            
            <h1 class="hero-title mb-6" data-aos="fade-up" data-aos-delay="100">
              Your Members Deserve Better Than This
            </h1>
            
            <p class="hero-subtitle mb-12" data-aos="fade-up" data-aos-delay="200">
              A digital clubhouse that keeps members engaged and connected—with all the tools to 
              run your club seamlessly built in. Modern member experience at honest prices.
            </p>
            
            <div class="hero-buttons" data-aos="fade-up" data-aos-delay="300">
              <v-btn
                to="/demo"
                x-large
                color="gold"
                class="mr-4 mb-4 hero-cta-primary"
                elevation="0"
              >
                Request a Demo
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn
                to="/why"
                x-large
                outlined
                color="gold"
                class="mb-4 hero-cta-secondary"
              >
                See Why We're Different
              </v-btn>
            </div>

            <div class="ornament-bottom mt-12" data-aos="fade-up" data-aos-delay="400">
              <div class="ornament-line"></div>
            </div>
          </v-col>
        </v-row>
      </v-container>
      
      <!-- Scroll Indicator -->
      <div class="scroll-indicator" data-aos="fade-in" data-aos-delay="800">
        <v-icon color="gold" class="scroll-icon">mdi-chevron-down</v-icon>
      </div>
    </section>

    <!-- Problem Section -->
    <section class="problem-section">
      <v-container>
        <v-row justify="center" class="mb-12">
          <v-col cols="12" md="10" lg="8" class="text-center">
            <h2 class="section-title mb-6" data-aos="fade-up">
              Private Clubs Have a Member Engagement Problem
            </h2>
            <p class="problem-text" data-aos="fade-up" data-aos-delay="100">
              Members pay their dues but don't feel connected. Communication is scattered. Younger members 
              aren't joining. Meanwhile, you're stuck with outdated software that your members hate using—
              and paying $2,000-$10,000 per month for the privilege.
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4" class="mb-6" v-for="(problem, index) in problems" :key="index">
            <div class="problem-card" :data-aos="'fade-up'" :data-aos-delay="index * 100">
              <v-icon size="40" color="error" class="mb-4">{{ problem.icon }}</v-icon>
              <h3 class="problem-title mb-3">{{ problem.title }}</h3>
              <p class="problem-description">{{ problem.description }}</p>
            </div>
          </v-col>
        </v-row>

        <div class="text-center mt-12" data-aos="fade-up">
          <div class="solution-badge">
            <v-icon color="gold" size="24" class="mr-2">mdi-lightbulb</v-icon>
            <span>We built a digital clubhouse that solves the engagement problem while handling operations.</span>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Digital Clubhouse Section -->
    <section class="clubhouse-section">
      <v-container>
        <div class="section-header text-center mb-16">
          <div class="section-ornament mb-4">
            <v-icon color="gold">mdi-rhombus-split</v-icon>
          </div>
          <h2 class="section-title mb-4" data-aos="fade-up">
            A Private Social Network for Your Club
          </h2>
          <p class="section-subtitle" data-aos="fade-up" data-aos-delay="100">
            Not just software that runs your club—a digital clubhouse where members connect, 
            engage, and feel part of your community.
          </p>
        </div>

        <v-row>
          <v-col
            v-for="(feature, index) in clubhouseFeatures"
            :key="index"
            cols="12"
            md="4"
            class="mb-8"
          >
            <v-card
              class="feature-card"
              elevation="0"
              :data-aos="'fade-up'"
              :data-aos-delay="index * 100"
            >
              <div class="feature-icon-wrapper mb-6">
                <v-icon size="48" color="gold">{{ feature.icon }}</v-icon>
              </div>
              <h3 class="feature-title mb-4">{{ feature.title }}</h3>
              <p class="feature-description">{{ feature.description }}</p>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Operations Section -->
    <section class="operations-section">
      <v-container>
        <div class="section-header text-center mb-16">
          <h2 class="section-title-white mb-4" data-aos="fade-up">
            And Yes, All The Club Management Tools Too
          </h2>
          <p class="section-subtitle-white" data-aos="fade-up" data-aos-delay="100">
            Golf club, racquet club, city club, or social club—whatever kind of club you run,
            member experience comes first, but you still need software that runs your operations.
            We've got you covered.
          </p>
        </div>

        <v-row>
          <v-col
            v-for="(feature, index) in operationsFeatures"
            :key="index"
            cols="12"
            md="4"
            class="mb-8"
          >
            <v-card
              class="operations-card"
              elevation="0"
              :data-aos="'fade-up'"
              :data-aos-delay="index * 100"
            >
              <div class="ops-icon-wrapper mb-4">
                <v-icon size="40" color="gold">{{ feature.icon }}</v-icon>
              </div>
              <h3 class="ops-title mb-3">{{ feature.title }}</h3>
              <p class="ops-description">{{ feature.description }}</p>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-center mt-12" data-aos="fade-up">
          <div class="operations-callout">
            <p class="callout-text mb-0">
              <strong>The difference?</strong> Legacy vendors built accounting systems and added social features. 
              We built a social network and added accounting. Both handle operations—only one keeps members engaged.
            </p>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Stats Section -->
    <section class="stats-section">
      <div class="stats-overlay"></div>
      <v-container>
        <v-row>
          <v-col
            v-for="(stat, index) in stats"
            :key="index"
            cols="12"
            sm="6"
            md="3"
            class="text-center"
          >
            <div class="stat-card" :data-aos="'zoom-in'" :data-aos-delay="index * 100">
              <div class="stat-number">{{ stat.number }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Why Choose Us Section -->
    <section class="why-section">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" class="mb-8 mb-md-0">
            <div class="why-content" data-aos="fade-right">
              <div class="section-ornament mb-4">
                <v-icon color="gold">mdi-rhombus-split</v-icon>
              </div>
              <h2 class="section-title mb-6">
                Why ClubLink Is Different
              </h2>
              <p class="why-description mb-6">
                Legacy systems were built for staff, with member features bolted on as an afterthought. 
                We flipped that model—built a member-first social network with all the operational tools 
                clubs need seamlessly integrated. The result? Members actually use it. Staff love it. 
                Clubs thrive.
              </p>
              
              <div
                v-for="(benefit, index) in benefits"
                :key="index"
                class="benefit-item mb-4"
              >
                <div class="benefit-icon">
                  <v-icon color="gold" size="20">mdi-check-circle</v-icon>
                </div>
                <div class="benefit-text">
                  <h4 class="benefit-title">{{ benefit.title }}</h4>
                  <p class="benefit-description">{{ benefit.description }}</p>
                </div>
              </div>

              <v-btn
                to="/why"
                large
                color="gold"
                class="mt-8"
                elevation="0"
              >
                See The Full Story
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="why-visual" data-aos="fade-left">
              <div class="visual-frame">
                <div class="visual-ornament top-left"></div>
                <div class="visual-ornament top-right"></div>
                <div class="visual-ornament bottom-left"></div>
                <div class="visual-ornament bottom-right"></div>
                <div class="visual-content">
                  <v-icon size="120" color="gold" style="opacity: 0.3;">mdi-account-heart</v-icon>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Pricing Section -->
    <section class="pricing-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <div class="pricing-content text-center" data-aos="fade-up">
              <v-icon color="gold" size="56" class="mb-6">mdi-cash-remove</v-icon>
              <h2 class="section-title mb-6">
                And It Doesn't Cost $100,000 a Year
              </h2>
              <p class="pricing-text mb-6">
                Here's the bonus: because we're cloud-native and modern, we don't have the overhead of 
                expensive data centers, legacy code maintenance, and armies of salespeople. We pass those 
                savings to you.
              </p>
              <p class="pricing-text mb-6">
                No $10k–$50k setup fees. No $2k–$10k monthly bills. No nickel-and-diming with "module fees" 
                and "integration charges." Just transparent pricing that actually makes sense for what you're getting.
              </p>
              <div class="pricing-callout">
                <p class="callout-text mb-0">
                  <strong>Better member experience. Better operations. Better price.</strong> That's what happens 
                  when you build software right from the start instead of maintaining 20-year-old systems.
                </p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Travel / Club Trailz Section -->
    <section class="travel-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="9">
            <div class="travel-card" data-aos="fade-up">
              <div class="travel-eyebrow mb-4">
                <v-icon color="gold" size="20" class="mr-2">mdi-domain</v-icon>
                <span>A Sister Platform From Code Forge Holdings</span>
              </div>

              <h2 class="section-title mb-6">
                Your Members Travel Too
              </h2>

              <p class="travel-description mb-10">
                Reciprocal agreements send your members into cities they don't
                know — where to eat, where to stay, who to ask. Club Trailz is
                where members of the reciprocal club world share exactly that,
                turning scattered word-of-mouth into one invite-only network.
                It's free to point your members toward, and it works
                alongside ClubLink, not instead of it.
              </p>

              <v-row class="travel-highlights mb-10">
                <v-col
                  v-for="(item, index) in travelHighlights"
                  :key="index"
                  cols="12"
                  sm="4"
                  class="text-center"
                >
                  <v-icon color="gold" size="32" class="mb-3">{{ item.icon }}</v-icon>
                  <h4 class="travel-highlight-title mb-2">{{ item.title }}</h4>
                  <p class="travel-highlight-description mb-0">{{ item.description }}</p>
                </v-col>
              </v-row>

              <v-btn
                href="https://clubtrailz.com"
                target="_blank"
                rel="noopener"
                x-large
                color="gold"
                class="travel-button"
                elevation="0"
              >
                Explore Club Trailz
                <v-icon right>mdi-open-in-new</v-icon>
              </v-btn>
              <p class="travel-footnote mt-4 mb-0">
                Worth mentioning to your members — especially the ones who travel often.
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center">
            <div data-aos="zoom-in">
              <v-icon color="gold" size="64" class="mb-6">mdi-calendar-check</v-icon>
              <h2 class="cta-title mb-6">
                See What Your Members Are Missing
              </h2>
              <p class="cta-description mb-10">
                Schedule a demo and we'll show you what a member-first club management system actually 
                looks like. Your members will thank you. Your staff will thank you. Your bottom line will thank you.
              </p>
              <v-btn
                to="/demo"
                x-large
                color="gold"
                class="cta-button"
                elevation="0"
              >
                Request Your Demo
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      problems: [
        {
          icon: 'mdi-account-off',
          title: 'Members Aren\'t Engaged',
          description: 'They pay dues but don\'t feel connected to the club or each other. Communication is fragmented. Engagement is low.'
        },
        {
          icon: 'mdi-cellphone-off',
          title: 'Software They Hate',
          description: 'Clunky interfaces from 2003. Members log in once a year to check a statement. Adoption rates under 40%.'
        },
        {
          icon: 'mdi-cash-multiple',
          title: 'Paying Too Much',
          description: '$10k-$50k setup fees, then $2k-$10k per month for software that doesn\'t solve the real problem: keeping members engaged.'
        }
      ],
      clubhouseFeatures: [
        {
          icon: 'mdi-chat-processing',
          title: 'Community Feed',
          description: 'Club updates, member interactions, event highlights. A living stream of club life that keeps members connected.'
        },
        {
          icon: 'mdi-account-heart',
          title: 'Member Connections',
          description: 'See who\'s at the club, connect with fellow members, build relationships. A digital extension of your clubhouse.'
        },
        {
          icon: 'mdi-calendar-heart',
          title: 'Engaging Events',
          description: 'Discover upcoming events, RSVP seamlessly, see who else is attending. Makes events feel communal, not transactional.'
        },
        {
          icon: 'mdi-cellphone-check',
          title: 'True Mobile-First',
          description: 'Apps that feel like Instagram, not legacy software crammed onto phones. Members actually enjoy using it.'
        },
        {
          icon: 'mdi-message-text',
          title: 'Unified Communication',
          description: 'One place for all club communication. No more scattered emails, texts, and phone calls trying to reach members.'
        },
        {
          icon: 'mdi-heart-pulse',
          title: 'High Adoption',
          description: '85-95% of members actively use ClubLink. Not because they have to—because they want to.'
        }
      ],
      operationsFeatures: [
        {
          icon: 'mdi-account-group',
          title: 'Member Management',
          description: 'Profiles, tiers, renewals. All the admin features you need, built around member experience.'
        },
        {
          icon: 'mdi-silverware-fork-knife',
          title: 'Dining & Reservations',
          description: 'Table booking and house accounts integrated into the member experience. Seamless, not separate.'
        },
        {
          icon: 'mdi-golf',
          title: 'Golf & Tee Times',
          description: 'Tee sheet management, tournaments, and leagues—built for golf and country clubs.'
        },
        {
          icon: 'mdi-tennis',
          title: 'Racquet Sports',
          description: 'Court bookings, ladders, and tournaments for tennis, racquetball, squash, and paddle clubs.'
        },
        {
          icon: 'mdi-bed',
          title: 'Overnight Lodging',
          description: 'Room reservations and guest stays for city clubs and clubs with on-site accommodations.'
        },
        {
          icon: 'mdi-calendar-star',
          title: 'Events & Private Parties',
          description: 'Create events, manage RSVPs and ticketing, and book private rooms—all part of the social experience.'
        },
        {
          icon: 'mdi-shopping-outline',
          title: 'Merchandise & Pro Shop',
          description: 'Sell apparel and equipment, manage inventory, and track pro shop orders in one place.'
        },
        {
          icon: 'mdi-food-takeout-box-outline',
          title: 'Takeout & To-Go Orders',
          description: 'Let members order ahead for pickup, separate from full-service dining reservations.'
        },
        {
          icon: 'mdi-clipboard-list-outline',
          title: 'Waitlist & Applications',
          description: 'Manage membership applications, sponsor references, and waitlists for exclusive clubs.'
        },
        {
          icon: 'mdi-invoice-text',
          title: 'Billing & Payments',
          description: 'Statements, payment processing. Support included—no extra fees.'
        },
        {
          icon: 'mdi-handshake-outline',
          title: 'Reciprocal Member Portal',
          description: 'A self-service portal where reciprocal members book dining, lodging, and activities, then manage and pay their own invoices. No more chasing anyone down for payment.'
        },
        {
          icon: 'mdi-chart-timeline-variant',
          title: 'Analytics & Reports',
          description: 'Real-time dashboards, engagement metrics, financial reports. Data that actually helps.'
        }
      ],
      stats: [
        { number: '90%+', label: 'Member Adoption' },
        { number: '2026', label: 'Built For Today' },
        { number: '100%', label: 'Cloud Native' },
        { number: 'Real', label: 'Human Support' }
      ],
      travelHighlights: [
        {
          icon: 'mdi-map-marker-radius',
          title: 'City Intelligence',
          description: 'Real recommendations from fellow reciprocal-club members — before your members even land.'
        },
        {
          icon: 'mdi-office-building-marker',
          title: 'City Chapters',
          description: 'A home base in every city with member activity, plus who\'s traveling there right now.'
        },
        {
          icon: 'mdi-shield-check',
          title: 'Verified & Invite-Only',
          description: 'Verified club affiliation, NDA-enforced, and encrypted — curated by membership, not an algorithm.'
        }
      ],
      benefits: [
        {
          title: 'Members Actually Use It',
          description: '85-95% adoption rates because it\'s genuinely useful and enjoyable, not mandatory admin software.'
        },
        {
          title: 'Drives Real Engagement',
          description: 'Members see club life happening, connect with others, and feel part of the community—not just paying dues.'
        },
        {
          title: 'All Operations Included',
          description: 'Billing, reservations, events, reciprocal member portals—everything you need, built around member experience.'
        },
        {
          title: 'Fair, Honest Pricing',
          description: 'No setup fees, no hidden charges, no nickel-and-diming. Modern technology at prices that make sense.'
        },
        {
          title: 'Built To Grow With You',
          description: 'Not a fixed, one-size-fits-all platform. Request new features and we\'ll build them for your club.'
        }
      ]
    };
  },
  mounted() {
    if (window.AOS) {
      window.AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true
      });
    }
  }
};
</script>

<style scoped>
/* Hero Section */
.hero-section {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 50%, #1a2e26 100%);
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.08) 0%, transparent 50%);
  pointer-events: none;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(212, 175, 55, 0.03) 2px,
    rgba(212, 175, 55, 0.03) 4px
  );
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
  height: 100%;
  padding-top: 80px;
  padding-bottom: 80px;
}

.hero-title {
  font-family: 'Playfair Display', serif;
  font-size: 64px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  letter-spacing: -1px;
}

.hero-subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  max-width: 700px;
  margin: 0 auto;
  letter-spacing: 0.5px;
}

.hero-cta-primary {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0 40px !important;
  color: #2d4a3e !important;
  transition: all 0.3s ease;
}

.hero-cta-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(212, 175, 55, 0.4) !important;
}

.hero-cta-secondary {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0 40px !important;
  border: 2px solid #d4af37;
  transition: all 0.3s ease;
}

.hero-cta-secondary:hover {
  background-color: rgba(212, 175, 55, 0.1);
  transform: translateY(-3px);
}

.ornament-top {
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.ornament-line {
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #d4af37, transparent);
  margin: 0 auto;
}

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(10px); }
}

/* Problem Section */
.problem-section {
  padding: 120px 0;
  background: white;
  position: relative;
}

.problem-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 110px;
  background: linear-gradient(180deg, #1a2e26 0%, rgba(26, 46, 38, 0) 100%);
  pointer-events: none;
}

.problem-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: #666;
  line-height: 1.9;
  max-width: 800px;
  margin: 0 auto;
}

.problem-card {
  background: #f8f6f1;
  padding: 32px;
  border-radius: 16px;
  text-align: center;
  height: 100%;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.problem-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-color: rgba(239, 83, 80, 0.2);
}

.problem-title {
  font-family: 'Playfair Display', serif;
  font-size: 20px;
  font-weight: 700;
  color: #2d4a3e;
}

.problem-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 15px;
  color: #666;
  line-height: 1.7;
  margin-bottom: 0;
}

.solution-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(201, 169, 97, 0.1));
  padding: 16px 32px;
  border-radius: 50px;
  border: 2px solid rgba(212, 175, 55, 0.3);
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #2d4a3e;
}

/* Clubhouse Section */
.clubhouse-section {
  padding: 120px 0;
  background-color: #f8f6f1;
  position: relative;
}

.section-header {
  margin-bottom: 80px;
}

.section-ornament {
  animation: rotate 20s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.section-title {
  font-family: 'Playfair Display', serif;
  font-size: 48px;
  font-weight: 700;
  color: #2d4a3e;
  letter-spacing: -0.5px;
}

.section-subtitle {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
}

.feature-card {
  background: white;
  padding: 40px;
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid transparent;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #d4af37, #c9a961);
  transform: scaleX(0);
  transition: transform 0.4s ease;
  transform-origin: left;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(45, 74, 62, 0.15);
  border-color: rgba(212, 175, 55, 0.2);
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(201, 169, 97, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s ease;
}

.feature-card:hover .feature-icon-wrapper {
  transform: scale(1.1) rotate(10deg);
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(201, 169, 97, 0.2));
}

.feature-title {
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 700;
  color: #2d4a3e;
  margin-bottom: 16px;
}

.feature-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 15px;
  color: #666;
  line-height: 1.8;
}

/* Operations Section */
.operations-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #2d4a3e 0%, #1a2e26 100%);
}

.section-title-white {
  font-family: 'Playfair Display', serif;
  font-size: 48px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.5px;
}

.section-subtitle-white {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
}

.operations-card {
  background: rgba(255, 255, 255, 0.05);
  padding: 32px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(212, 175, 55, 0.2);
  transition: all 0.3s ease;
  height: 100%;
}

.operations-card:hover {
  transform: translateY(-8px);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(212, 175, 55, 0.4);
}

.ops-icon-wrapper {
  width: 70px;
  height: 70px;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ops-title {
  font-family: 'Playfair Display', serif;
  font-size: 20px;
  font-weight: 700;
  color: #d4af37;
}

.ops-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin-bottom: 0;
}

.operations-callout {
  background: rgba(212, 175, 55, 0.1);
  padding: 32px 40px;
  border-radius: 12px;
  border: 2px solid rgba(212, 175, 55, 0.3);
  max-width: 800px;
  margin: 0 auto;
}

.operations-callout .callout-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 17px;
  color: #ffffff;
  line-height: 1.8;
  font-weight: 500;
}

.operations-callout .callout-text strong {
  color: #d4af37;
}

/* Stats Section */
.stats-section {
  padding: 100px 0;
  background: white;
  position: relative;
  overflow: hidden;
}

.stats-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 70% 50%, rgba(212, 175, 55, 0.05) 0%, transparent 50%);
}

.stats-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(180deg, #1a2e26 0%, rgba(26, 46, 38, 0) 100%);
  pointer-events: none;
}

.stat-card {
  position: relative;
  z-index: 1;
}

.stat-number {
  font-family: 'Playfair Display', serif;
  font-size: 56px;
  font-weight: 700;
  color: #d4af37;
  margin-bottom: 12px;
}

.stat-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #2d4a3e;
  text-transform: uppercase;
  letter-spacing: 2px;
}

/* Why Section */
.why-section {
  padding: 120px 0;
  background-color: #f8f6f1;
}

.why-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  color: #666;
  line-height: 1.9;
}

.benefit-item {
  display: flex;
  gap: 20px;
  padding: 20px;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.benefit-item:hover {
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.benefit-icon {
  flex-shrink: 0;
}

.benefit-title {
  font-family: 'Playfair Display', serif;
  font-size: 18px;
  font-weight: 700;
  color: #2d4a3e;
  margin-bottom: 8px;
}

.benefit-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  margin-bottom: 0;
}

.why-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.visual-frame {
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(201, 169, 97, 0.1));
  border-radius: 16px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.visual-ornament {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 2px solid #d4af37;
}

.visual-ornament.top-left {
  top: -10px;
  left: -10px;
  border-right: none;
  border-bottom: none;
}

.visual-ornament.top-right {
  top: -10px;
  right: -10px;
  border-left: none;
  border-bottom: none;
}

.visual-ornament.bottom-left {
  bottom: -10px;
  left: -10px;
  border-right: none;
  border-top: none;
}

.visual-ornament.bottom-right {
  bottom: -10px;
  right: -10px;
  border-left: none;
  border-top: none;
}

/* Pricing Section */
.pricing-section {
  padding: 120px 0;
  background: white;
}

.pricing-content {
  max-width: 800px;
  margin: 0 auto;
}

.pricing-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: #666;
  line-height: 1.9;
}

.pricing-callout {
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.08), rgba(201, 169, 97, 0.08));
  padding: 32px;
  border-radius: 12px;
  border-left: 4px solid #d4af37;
  margin-top: 40px;
}

.pricing-callout .callout-text {
  font-family: 'Montserrat', sans-serif;
  font-size: 17px;
  color: #2d4a3e;
  line-height: 1.8;
}

.pricing-callout .callout-text strong {
  color: #d4af37;
  font-weight: 700;
}

/* Travel Section */
.travel-section {
  padding: 120px 0;
  background-color: #f8f6f1;
  position: relative;
}

.travel-card {
  background: white;
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-top: 4px solid #d4af37;
  border-radius: 16px;
  padding: 64px 56px;
  text-align: center;
}

.travel-eyebrow {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #2d4a3e;
}

.travel-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 17px;
  color: #666;
  line-height: 1.9;
  max-width: 680px;
  margin: 0 auto;
}

.travel-highlights {
  max-width: 780px;
  margin: 0 auto;
}

.travel-highlight-title {
  font-family: 'Playfair Display', serif;
  font-size: 18px;
  font-weight: 700;
  color: #2d4a3e;
}

.travel-highlight-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  color: #666;
  line-height: 1.7;
}

.travel-button {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0 40px !important;
  color: #2d4a3e !important;
  transition: all 0.3s ease;
}

.travel-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(212, 175, 55, 0.4) !important;
}

.travel-footnote {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  color: #999;
  font-style: italic;
}

/* CTA Section */
.cta-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 100%);
  position: relative;
}

.cta-title {
  font-family: 'Playfair Display', serif;
  font-size: 48px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.5px;
}

.cta-description {
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  max-width: 600px;
  margin: 0 auto;
}

.cta-button {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0 50px !important;
  color: #2d4a3e !important;
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(212, 175, 55, 0.5) !important;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-title {
    font-size: 48px;
  }
  
  .hero-subtitle {
    font-size: 16px;
  }
  
  .section-title,
  .section-title-white {
    font-size: 36px;
  }
  
  .cta-title {
    font-size: 36px;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 15px;
  }
  
  .section-title,
  .section-title-white {
    font-size: 28px;
  }
  
  .cta-title {
    font-size: 28px;
  }
  
  .stat-number {
    font-size: 42px;
  }
  
  .problem-text,
  .pricing-text {
    font-size: 16px;
  }
  
  .solution-badge {
    font-size: 14px;
    padding: 12px 24px;
  }

  .travel-card {
    padding: 40px 24px;
  }
}
</style>